/** @format */

import {TFunction} from 'i18next';

import {Chart} from 'components/charts/multiline-chart/model/charts';

import {WidgetSettings} from 'components/PropertyWidget/model';

import {getMonthGenitive} from 'helpers/dateHelpers';

import {PointName} from 'components/charts/multiline-chart/model/common';

import {DEFAULT_CALC_METHOD} from '../constants/common';
import {WIDGETS_LIST} from '../constants/widgets';
import {ChartIds} from '../models/chartIds';
import {GraphComplexValuesList} from '../models/common';
import {WidgetBlockColor} from '../models/widgets';
import {StatsSensorsResult} from './calculate-stats/statsSensors';
import getStatsTypeA from './widget-stats/getStatsTypeA';
import getStatsTypeB from './widget-stats/getStatsTypeB';
import getStatsTypeC from './widget-stats/getStatsTypeC';
import getStatsTypeD from './widget-stats/getStatsTypeD';
import {getStatsTypeSensor} from './widget-stats/getStatsTypeSensor';
import {getStatsTypeScatterLogs} from './widget-stats/getStatsTypeScatterLogs';
import {getStatsTypeScatterNotes} from './widget-stats/getStatsTypeScatterNotes';

export interface GetWidgetSettingsOptions {
  chart: Chart<ChartIds>;
  todayPointName: PointName;
  graphComplexValuesList: GraphComplexValuesList;
  statsSensorsResult?: StatsSensorsResult;
  sensorColors?: WidgetBlockColor;
  companyId: string;
  t: TFunction;
  startDate: string;
  endDate: string;
}

const getWidgetSettings = ({
  chart,
  todayPointName,
  graphComplexValuesList,
  statsSensorsResult,
  sensorColors,
  companyId,
  t,
  startDate,
  endDate,
}: GetWidgetSettingsOptions): WidgetSettings => {
  const chartId = chart.chartId;
  const widget = WIDGETS_LIST[chartId];
  const {showSumDayButton, type} = widget;
  const calcMethod = showSumDayButton ? DEFAULT_CALC_METHOD : undefined;
  const data = chart.chartsData;

  const result: WidgetSettings = {};

  if (calcMethod) {
    result.calcMethod = calcMethod;
  }

  if (widget.hide) {
    result.hide = true;
  }

  const now = new Date();
  const day = now.getDate();
  const month = getMonthGenitive(now.getMonth());
  const to = day === 2 ? 'ко' : 'к';

  switch (type) {
    case 'TypeA': {
      result.stats = getStatsTypeA({
        data,
        day,
        month,
        to,
        widget,
      });
      break;
    }
    case 'TypeB': {
      result.stats = getStatsTypeB({
        data,
        todayPointName,
        widget,
        graphComplexValuesList,
      });
      break;
    }
    case 'TypeC': {
      result.stats = getStatsTypeC({
        data,
        todayPointName,
        widget,
        graphComplexValuesList,
      });
      break;
    }
    case 'TypeD': {
      result.stats = getStatsTypeD({
        data,
        day,
        month,
        to,
        widget,
      });
      break;
    }
    case 'sensor': {
      result.stats = getStatsTypeSensor({
        statsSensorsResult,
        chartId,
        sensorColors,
        companyId,
        t,
      });
      break;
    }
    case 'scatter': {
      if (chartId === 'logs') {
        result.stats = getStatsTypeScatterLogs({
          data,
          startDate,
          endDate,
        });
      } else if (chartId === 'notes') {
        result.stats = getStatsTypeScatterNotes({
          data,
        });
      }
      break;
    }
  }

  return result;
};

export default getWidgetSettings;
